import React, { useCallback, useEffect, useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Download } from 'lucide-react';

import { DownloadFormat, Fonts, TextAlignment, DocumentDownloadOptions } from '../../../../backend/src/types/types';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/ShadCnSelect';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { FONT_FAMILY_OPTIONS, TEXT_ALIGNMENT_OPTIONS } from '@/constants/downloadOptions';
import { cn } from '@/helpers/shadCnUtils';

interface DownloadWrittenModalProps {
  downloadTitle: string;
  loadingDownload: boolean;
  showDocumentOptions?: boolean;
  showStyle?: boolean;
  showSourceDocumentsOption?: boolean;
  handleDownload: ({
    title,
    style,
    downloadFormat,
    documentOptions,
    includeSourceDocuments,
  }: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
  }) => void;
}

export const DownloadWrittenModal = ({
  downloadTitle,
  loadingDownload,
  handleDownload,
  showStyle = true,
  showDocumentOptions = false,
  showSourceDocumentsOption = false,
}: DownloadWrittenModalProps) => {
  const [title, setTitle] = useState(downloadTitle);
  const [style, setStyle] = useState('');
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);
  const [documentOptions, setDocumentOptions] = useState<DocumentDownloadOptions>({
    fontFamily: Fonts.TimesNewRoman,
    textAlignment: TextAlignment.Left,
    includeReferences: true,
  });

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleChangeStyle = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStyle(e.target.value);
  }, []);

  const handleDownloadClick = () => {
    handleDownload({
      title,
      style,
      downloadFormat: DownloadFormat.DOCX,
      documentOptions,
      includeSourceDocuments,
    });
  };

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>
          <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div className="px-8 py-6">
          <div className="mb-6">
            <div className="mb-4 flex flex-col gap-2">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                File Name
              </Label>
              <Input
                id="title"
                onChange={handleChangeTitle}
                value={title}
                placeholder="Enter a title for your file..."
                className="w-full"
              />
            </div>
            {showStyle && (
              <div className="mb-6 flex flex-col gap-2">
                <Label htmlFor="style" className="text-sm font-medium text-gray-700">
                  Document Style (optional)
                </Label>
                <Textarea
                  id="style"
                  onChange={handleChangeStyle}
                  value={style}
                  placeholder="Write instructions or paste examples of how you would like each fact to be formatted. For example: a particular date format, to use more direct quotes from source text..."
                  className="h-32 max-h-52 w-full"
                />
                <p className="text-xs text-gray-500">
                  Wexler rewrites each fact according to the style or format you've instructed above. Visual style
                  instructions will not be reflected in the download at this time.
                </p>
              </div>
            )}

            {showDocumentOptions && (
              <>
                <div className="mb-4 space-y-2">
                  <Label className="text-sm font-medium">Font</Label>
                  <Select
                    value={documentOptions?.fontFamily}
                    onValueChange={(value: Fonts) => setDocumentOptions({ ...documentOptions, fontFamily: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_FAMILY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mb-4 space-y-2">
                  <Label className="text-sm font-medium">Text Alignment</Label>
                  <Select
                    value={documentOptions?.textAlignment}
                    onValueChange={(value: TextAlignment) =>
                      setDocumentOptions({ ...documentOptions, textAlignment: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select alignment" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEXT_ALIGNMENT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mb-6 flex items-center justify-between">
                  <div className="flex flex-col">
                    <Label className="text-sm font-medium">Include References</Label>
                    <span className="mt-1 text-xs text-gray-500">
                      {documentOptions?.includeReferences
                        ? 'References section will be added at the end'
                        : 'No references will be included'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Label
                      className={cn(
                        'm-0 px-2 text-sm text-gray-700 transition-colors duration-150',
                        !documentOptions?.includeReferences && 'text-brandSecondary',
                      )}
                    >
                      No
                    </Label>
                    <Switch
                      className="bg-gray-100"
                      checked={documentOptions?.includeReferences}
                      onCheckedChange={(checked) =>
                        setDocumentOptions({ ...documentOptions, includeReferences: checked })
                      }
                    />
                    <Label
                      className={cn(
                        'm-0 text-sm text-gray-700 transition-colors duration-150',
                        documentOptions?.includeReferences && 'text-brandSecondary',
                      )}
                    >
                      Yes
                    </Label>
                  </div>
                </div>
              </>
            )}

            {showSourceDocumentsOption && (
              <div className="mb-4 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="includeSourceDocuments"
                  checked={includeSourceDocuments}
                  onChange={(e) => setIncludeSourceDocuments(e.target.checked)}
                  className="text-brandSecondary focus:ring-brandSecondary mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2"
                />
                <label htmlFor="includeSourceDocuments" className="cursor-pointer text-sm text-gray-700">
                  <div className="font-medium">Include source documents</div>
                  <div className="text-xs text-gray-500">
                    Download a zip containing the chronology and all referenced documents
                  </div>
                </label>
              </div>
            )}

            <div className="my-6 border-t border-gray-200"></div>

            <Button
              onClick={handleDownloadClick}
              text="Download"
              type="primary"
              rounded="md"
              className="w-full justify-center py-2.5"
              icon={<Download className="mr-2" />}
            />
          </div>
        </div>
      )}
    </>
  );
};
